import Modal from "@/components/shared/ui/Modal";
import Badge from "@/components/shared/ui/Badge";
import Button from "@/components/shared/ui/Button";
import { ExternalLink, Code, Info } from "lucide-react";

interface Tool {
  id: string;
  name: string;
  client?: string;
  purpose?: string;
  features?: string[];
  technical_summary?: string;
  link?: string;
  thumbnail?: string;
  additional_detail?: string;
}

interface ToolModalProps {
  isOpen: boolean;
  onClose: () => void;
  tool: Tool | null;
}

export default function ToolModal({ isOpen, onClose, tool }: ToolModalProps) {
  if (!tool) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={tool.name} maxWidth="2xl">
      <div
        className="space-y-6"
        style={{ fontFamily: "'Courier New', monospace" }}
      >
        {/* Thumbnail & Client */}
        <div className="flex items-start gap-6 pb-6 border-b-2 border-black">
          <div className="text-8xl">{tool.thumbnail || "📦"}</div>
          <div className="flex-1">
            {tool.client && (
              <div className="mb-2">
                <Badge variant="info" size="md">
                  {tool.client}
                </Badge>
              </div>
            )}
            {tool.purpose && (
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                {tool.purpose}
              </p>
            )}
          </div>
        </div>

        {/* Features */}
        {tool.features && tool.features.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-5 h-5 text-black" />
              <h3 className="text-xl font-bold text-black">Key Features</h3>
            </div>
            <ul className="space-y-2 pl-4">
              {tool.features.map((feature, index) => (
                <li
                  key={index}
                  className="text-gray-700 text-sm md:text-base flex items-start gap-2"
                >
                  <span className="text-black font-bold mt-1">•</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Technical Summary */}
        {tool.technical_summary && (
          <div className="bg-gray-50 border-2 border-black p-4">
            <div className="flex items-center gap-2 mb-3">
              <Code className="w-5 h-5 text-black" />
              <h3 className="text-lg font-bold text-black">
                Technical Summary
              </h3>
            </div>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              {tool.technical_summary}
            </p>
          </div>
        )}

        {/* Additional Detail */}
        {tool.additional_detail && (
          <div className="bg-yellow-50 border-2 border-yellow-500 p-4">
            <p className="text-sm md:text-base text-gray-800 leading-relaxed">
              <span className="font-bold text-black">Note: </span>
              {tool.additional_detail}
            </p>
          </div>
        )}

        {/* Link Button */}
        {tool.link && (
          <div className="pt-4 border-t-2 border-black">
            <Button
              variant="primary"
              size="lg"
              onClick={() => window.open(tool.link, "_blank")}
              className="w-full md:w-auto"
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              Visit Live Demo
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
